import React from 'react';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import styles from '../shared/invoiceStyles';
import { formatInr, formatInvoiceDate, formatPaymentMethod } from '../shared/formatInr';

const isAbsoluteImage = (value) => /^https?:\/\//i.test(String(value || '').trim());

function AddressBlock({ title, name, lines, email, phone }) {
  return (
    <View style={styles.col}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.bold}>{name || '-'}</Text>
      {(lines || []).map((line) => (
        <Text key={line} style={styles.muted}>{line}</Text>
      ))}
      {phone ? <Text style={styles.muted}>{phone}</Text> : null}
      {email ? <Text style={styles.muted}>{email}</Text> : null}
    </View>
  );
}

function EcOrderInvoiceDocument({ order, branding }) {
  const customerName = [order?.FirstName, order?.LastName].filter(Boolean).join(' ').trim() || '-';
  const addressLines = [
    order?.BillingAddress1,
    order?.BillingAddress2,
    [order?.City, order?.Zipcode].filter(Boolean).join(' '),
    order?.BillingCountry || order?.ShippingCountry,
  ].filter(Boolean);
  const items = Array.isArray(order?.Items) ? order.Items : [];
  const logoUrl = isAbsoluteImage(branding?.logoUrl) ? branding.logoUrl : '';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.company}>
            {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
            <Text style={styles.companyName}>{branding?.companyName || 'Invoice'}</Text>
            {(branding?.addressLines || []).map((line) => (
              <Text key={line} style={styles.muted}>{line}</Text>
            ))}
            {branding?.phone ? <Text style={styles.muted}>{branding.phone}</Text> : null}
            {branding?.website ? <Text style={styles.muted}>{branding.website}</Text> : null}
          </View>
          <View style={styles.invoiceMeta}>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text>Date: {formatInvoiceDate(order?.OrderDate)}</Text>
            <Text>Order #: {order?.OrderNumber || `#${order?.OrderId}`}</Text>
            <Text>Status: {order?.OrderStatus || '-'}</Text>
          </View>
        </View>

        <View style={styles.row}>
          <AddressBlock
            title="Bill To"
            name={customerName}
            lines={addressLines}
            email={order?.Email}
            phone={order?.Phone}
          />
          <AddressBlock
            title="Ship To"
            name={customerName}
            lines={addressLines}
            email={order?.Email}
            phone={order?.Phone}
          />
        </View>

        <View style={styles.tableHeader}>
          <Text style={[styles.cellDesc, styles.bold]}>Description</Text>
          <Text style={[styles.cellQty, styles.bold]}>Qty</Text>
          <Text style={[styles.cellMoney, styles.bold]}>Unit Price</Text>
          <Text style={[styles.cellMoney, styles.bold]}>Line Total</Text>
        </View>
        {items.length ? items.map((item) => (
          <View key={item.OrderItemId || item.ProductName} style={styles.tableRow}>
            <Text style={styles.cellDesc}>{item.ProductName || '-'}</Text>
            <Text style={styles.cellQty}>{item.Quantity ?? 0}</Text>
            <Text style={styles.cellMoney}>{formatInr(item.UnitPrice)}</Text>
            <Text style={styles.cellMoney}>{formatInr(item.LineTotal)}</Text>
          </View>
        )) : (
          <View style={styles.tableRow}>
            <Text>No line items recorded.</Text>
          </View>
        )}

        <View style={styles.notes}>
          <Text style={styles.sectionTitle}>Payment</Text>
          <Text>Method: {formatPaymentMethod(order?.PaymentMethod)}</Text>
          <Text style={styles.muted}>Payment status: {order?.PaymentStatus || '-'}</Text>
        </View>

        <View style={styles.totals}>
          <View style={styles.totalRow}>
            <Text>Subtotal</Text>
            <Text>{formatInr(order?.SubTotal ?? order?.TotalAmount)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.bold}>Total</Text>
            <Text style={styles.bold}>{formatInr(order?.TotalAmount)}</Text>
          </View>
        </View>

        <View style={styles.notes}>
          <Text style={styles.sectionTitle}>Notes</Text>
          <Text>{order?.Notes || '-'}</Text>
        </View>
        <Text style={styles.footer}>Thank you for your business!</Text>
      </Page>
    </Document>
  );
}

export default EcOrderInvoiceDocument;
